"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import { WalletForm } from "@/components/wallet-form"
import { RandomTower } from "@/components/random-tower"
import { MobileRandomTower } from "@/components/mobile-random-tower"
import { AnimatedDiceIcon } from "@/components/animated-dice-icon"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useMobile } from "@/hooks/use-mobile"
// Import the keyboard shortcuts tooltip
import { KeyboardShortcutsTooltip } from "@/components/keyboard-shortcuts-tooltip"
// Import keyboard utilities
import { HOTKEYS, matchesPhysicalKey } from "@/lib/keyboard-utils"
// Import the COLLECTIONS array at the top of the file
import { COLLECTIONS } from "@/lib/api"
// Import the badge carousel component
import { BadgeCarousel } from "@/components/badge-carousel"
// Import the Product Hunt badge component
import { ProductHuntBadge } from "@/components/product-hunt-badge"
// Import the MintedByCollectors component
import { MintedByCollectors } from "@/components/minted-by-collectors"

// Add the import for RandomTowerRef
import type { RandomTowerRef } from "@/types/tower"

// Animation variants for staggered reveal
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
}

export default function Home() {
  const [randomizeCount, setRandomizeCount] = useState(0)
  const [isGenerating, setIsGenerating] = useState(false)
  const [diceCount, setDiceCount] = useState(5)
  const randomTowerRef = useRef<RandomTowerRef | null>(null)
  const mobileRandomTowerRef = useRef<RandomTowerRef | null>(null)
  const isMobile = useMobile()
  const autoShuffleTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Framer Motion typing workaround for className support
  const MotionDiv: any = motion.div
  const MotionH1: any = motion.h1
  const MotionP: any = motion.p

  const handleRandomize = () => {
    // Don't allow multiple randomizations at once
    if (isGenerating || !randomTowerRef.current) return

    setIsGenerating(true)

    // Update the dice count to a random number between 1 and 6
    setDiceCount(Math.floor(Math.random() * 6) + 1)

    // Call the generateRandomTower method on the RandomTower component
    randomTowerRef.current.generateRandomTower()

    // Reset the generating state after animation completes
    setTimeout(() => {
      setIsGenerating(false)
    }, 800) // 0.8 second delay before re-enabling
  }

  // Function to reset auto-shuffle timer
  const resetAutoShuffleTimer = useCallback(() => {
    // Clear existing timer
    if (autoShuffleTimerRef.current) {
      clearTimeout(autoShuffleTimerRef.current)
    }

    // Only set timer for mobile
    if (isMobile) {
      // Set new timer for 5 seconds
      autoShuffleTimerRef.current = setTimeout(() => {
        if (mobileRandomTowerRef.current) {
          mobileRandomTowerRef.current.generateRandomTower()
          // Restart timer after auto-shuffle
          resetAutoShuffleTimer()
        }
      }, 5000) // 5 seconds
    }
  }, [isMobile])

  // Update the handleMobileRandomize function to properly call the ref method
  const handleMobileRandomize = () => {
    if (!mobileRandomTowerRef.current) return

    // Directly call the generateRandomTower method on the ref
    mobileRandomTowerRef.current.generateRandomTower()
    
    // Reset the auto-shuffle timer
    resetAutoShuffleTimer()
  }

  // Auto-shuffle effect for mobile
  useEffect(() => {
    // Start auto-shuffle timer only on mobile
    if (isMobile) {
      resetAutoShuffleTimer()
    }

    // Cleanup on unmount
    return () => {
      if (autoShuffleTimerRef.current) {
        clearTimeout(autoShuffleTimerRef.current)
      }
    }
  }, [isMobile, resetAutoShuffleTimer])

  // Update the keyboard event handler to guarantee consistent execution
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      // Use physical key position for 'R' key to randomize tower
      if (matchesPhysicalKey(event, HOTKEYS.RANDOMIZE)) {
        // Update the dice count to a random number between 1 and 6
        setDiceCount(Math.floor(Math.random() * 6) + 1)

        // Always reference ref.current within useEffect for stability
        const mobileTowerRef = mobileRandomTowerRef.current
        const desktopTowerRef = randomTowerRef.current

        if (isMobile && mobileTowerRef) {
          mobileTowerRef.generateRandomTower()
          // Reset auto-shuffle timer when user manually shuffles
          resetAutoShuffleTimer()
        } else if (desktopTowerRef) {
          handleRandomize()
        }
      }

      // Use physical key position for 'G' key to activate Easter Egg
      if (matchesPhysicalKey(event, HOTKEYS.EASTER_EGG)) {
        const mobileTowerRef = mobileRandomTowerRef.current
        const desktopTowerRef = randomTowerRef.current

        if (isMobile && mobileTowerRef && mobileTowerRef.activateEasterEgg) {
          mobileTowerRef.activateEasterEgg()
        } else if (desktopTowerRef && desktopTowerRef.activateEasterEgg) {
          desktopTowerRef.activateEasterEgg()
        }
      }
    }

    // Add event listener
    window.addEventListener("keydown", handleKeyDown)

    // Clean up
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isGenerating, isMobile, resetAutoShuffleTimer])

  return (
    <main className="flex flex-1 flex-col items-center p-4 md:p-8 lg:p-12 pt-28 md:pt-24 lg:pt-24 relative">
      <div className="w-full pt-2">
        {/* Mobile layout - top-first horizontal alignment */}
        <div className="md:hidden w-full flex flex-col items-center">
          {/* Title - full width with horizontal alignment */}
          <MotionDiv
            className="w-full max-w-[384px] mb-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <MotionDiv variants={itemVariants} className="w-full flex justify-center">
              <BadgeCarousel 
                className="mb-2"
                badgeClassName="inline-flex items-center bg-gray-50 text-gray-700 pl-1 pr-2 py-0.5 border border-gray-200 rounded-full text-xs font-funnel font-medium uppercase hover:bg-white"
              />
            </MotionDiv>
            <MotionH1
              className="text-3xl sm:text-4xl font-bold text-gray-900 text-center tracking-tight font-funnel w-full"
              variants={itemVariants}
              style={{ maxWidth: "100%" }}
            >
              Claim Your Totem
            </MotionH1>
          </MotionDiv>

          {/* Mobile random tower - positioned above the form */}
          <div 
            className="w-full max-w-[384px] mb-6 cursor-pointer active:opacity-90 transition-opacity"
            onClick={handleMobileRandomize}
            role="button"
            aria-label="Shuffle tower"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleMobileRandomize()
              }
            }}
          >
            <MotionDiv
              className="w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <MobileRandomTower ref={mobileRandomTowerRef} />
            </MotionDiv>
          </div>

          {/* Form container */}
          <MotionDiv
            className="w-full max-w-[384px] mt-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Form with consistent horizontal alignment */}
            <MotionDiv variants={itemVariants} className="w-full">
              <WalletForm />
            </MotionDiv>
          </MotionDiv>

          {/* Mobile minted by collectors section - below form */}
          <MotionDiv
            className="md:hidden w-full flex flex-col items-center mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <MintedByCollectors />
          </MotionDiv>

        </div>

        {/* Desktop layout - 4-column grid (fixed 384px + 3 flexible) */}
        <div className="hidden md:grid md:grid-cols-[384px_1fr_1fr_1fr] gap-8 items-start w-full">
          {/* Left column: Wrapper for form and minted-by */}
          <div className="flex flex-col gap-4 w-[384px] min-w-[384px]">
            {/* Gray container with form and options */}
            <MotionDiv
              className="flex flex-col items-start p-6 w-full rounded-3xl bg-gray-100/90 backdrop-blur-sm"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <MotionDiv variants={itemVariants}>
                <BadgeCarousel
                  className="mb-2"
                  badgeClassName="inline-flex items-center bg-gray-50 text-gray-700 pl-1 pr-2.5 py-0.5 border border-gray-200 rounded-full text-xs font-funnel font-medium uppercase hover:bg-white"
                />
              </MotionDiv>
              {/* H1 title with animation */}
              <MotionH1
                className="text-4xl md:text-[36px] font-bold text-gray-900 mb-4 text-left tracking-tight leading-[1.1] font-funnel w-full"
                variants={itemVariants}
              >
                Claim Your NFT Power Totem
              </MotionH1>

              {/* Description with animation */}
              <MotionP className="text-gray-600 mb-6 text-left w-full" variants={itemVariants}>
                Enter a wallet address or ENS name to discover NFTs from{" "}
                <span className="font-semibold">{COLLECTIONS.length} handpicked collections</span>.
              </MotionP>

              {/* Form with animation */}
              <MotionDiv variants={itemVariants} className="w-full">
                <WalletForm />
              </MotionDiv>

              {/* Options block - similar to results page */}
              <MotionDiv variants={itemVariants} className="w-full mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Options</span>
                  <div className="flex items-center gap-2">
                    <KeyboardShortcutsTooltip showEasterEgg={true}>
                      <Button
                        variant="outline"
                        className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-100 transition-all duration-300 h-10 px-4 flex items-center gap-2"
                        onClick={handleRandomize}
                        title="Randomize Tower Order (R)"
                        disabled={isGenerating}
                      >
                        <AnimatedDiceIcon dotCount={diceCount} />
                        <span className="text-xs font-medium">Shuffle</span>
                      </Button>
                    </KeyboardShortcutsTooltip>
                  </div>
                </div>
              </MotionDiv>
            </MotionDiv>

            {/* Minted by collectors section - below gray container in first column */}
            <MotionDiv
              className="flex flex-col items-start w-full"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <MintedByCollectors />
            </MotionDiv>
          </div>

          {/* Middle column: Empty for spacing */}

          {/* Right column: Empty for spacing */}

          {/* Far right column: Random tower visualization - only visible on desktop */}
          <div className="hidden md:flex md:col-span-3 md:justify-center md:items-center md:mt-10 h-[700px]">
            <RandomTower
              ref={randomTowerRef}
              simplifiedAnimation={false} // Use standard animation on desktop
            />
          </div>
        </div>
      </div>
    </main>
  )
}
